import { hashHistory } from 'react-router';

import { getInstance as getStorageManager } from 'storage/manager';
import { getInstance as getDataSources } from 'datasources';
import { hideModal, setStatus as setModalStatus, ModalStatus } from 'store/actions/modal';
import { setRedirectUrl, attemptSessionRefresh } from 'store/actions/session';
import { fetchLinearChannels } from 'store/actions/linearchannels';
import {
    setStatus as setPaymentStatus,
    PaymentStatus
} from 'store/actions/payment';
import AnalyticsEventsManager from 'utils/firebase/index.js';
import { isMobile } from 'lib/checkuseros';
import { clearNewRegistrationData } from '../newRegistration';

const locationHrefForIgnoringAnalyticsEvent = 'register/step-two';

// Action types
const ActionTypes = {
    SET_LOGIN_FORM_STATUS: 'SET_LOGIN_FORM_STATUS',
    SET_LOGIN_FORM_DATA: 'SET_LOGIN_FORM_DATA',
    SET_LOGIN_FORM_ERROR: 'SET_LOGIN_FORM_ERROR'
},

    // Status values
    LoginStatus = {
        INITIAL: 'initial',
        USER_EXISTS: 'user exists',
        REQUEST_SUCCESS: 'request-success',
        REQUEST_FAILURE: 'request-failure'
    },

    storageManager = getStorageManager();

/**
 * Set status action.
 *
 * @param {string} status - New status of the component.
 * @returns {Object} Action type and the new status.
 */
function setStatus(status = LoginStatus.INITIAL) {

    return {
        type: ActionTypes.SET_LOGIN_FORM_STATUS,
        status
    };
}

/**
 * Set form data action.
 *
 * @param {Object} [initialValues] - Initial values of the form.
 * @param {string} initialValues.username - User's name.
 * @param {string} initialValues.password - User's password.
 * @returns {Object} Action type, user's name and password.
 */
function setFormData(initialValues) {

    return {
        type: ActionTypes.SET_LOGIN_FORM_DATA,
        initialValues
    };
}

/**
 * Set error action.
 *
 * @param {Object} error - Error object with reason message.
 * @returns {Object} Action type and error object.
 */
function setError(error) {

    return {
        type: ActionTypes.SET_LOGIN_FORM_ERROR,
        formError: error
    };
}

/**
 * Log out user.
 *
 * @param {bool} preventClosingModal - Prevent closing error modal.
 * @returns {Function} Function that returns a promise
 * resolving to response to the log out call.
 */
function attemptLogout(preventClosingModal) {

    return (dispatch, getState) => {  // eslint-disable-line no-unused-vars

        let dataSources = getDataSources();

        return new Promise((res, rej) => {
            dataSources.logout()
                .then(() => {
                    res();
                    if (!preventClosingModal) {
                        dispatch(hideModal());
                    }
                    dispatch(clearNewRegistrationData());
                    localStorage.clear()
                    hashHistory.push('/');
                  
                    dispatch(setModalStatus(ModalStatus.HIDE));

                    // dispatch(attemptSessionRefresh(false)).then(() => {
                    //     dispatch(setPaymentStatus(PaymentStatus.REQUEST_SUCCESS));
                    // });

                });
        });
    };
}

function sendHooligansEvent(props) {
    const { event, gaEventCategory, gaEventAction, gaUserID, gaEventLabel } = props;

    window.dataLayer.push({
        event,
        gaEventCategory,
        gaEventAction,
        gaUserID,
        gaEventLabel
    });
}

function loginUser(username, password, pureLogin, preventRedirect) {
    return new Promise(async (resolve, reject) => {
        const dataSources = getDataSources();

        try {
            await dataSources.authenticateWithCredentials(
                {
                    username,
                    password,
                },
                pureLogin
            );

            resolve();
        } catch (error) {
            reject(error.result.error);
        }
    });
}

/**
 * Attempt to login action.
 *
 * @param {string} username - User's name.
 * @param {string} password - User's password.
 * @param {boolean} pureLogin - In case it is true,
 * we are not redirecting after login and not calling for payment details.
 * @param {boolean} [preventRedirect] - Prevent redirect after login.
 * @returns {Function} Function that returns a promise
 * resolving to login response.
 */
function attemptLogin(username, password, pureLogin, preventRedirect) {
    return (dispatch, getState) => {
        let dataSources = getDataSources();

        dispatch(setModalStatus(ModalStatus.LOADING));

        return dataSources.authenticateWithCredentials({
            username: username,
            password: password
        }, pureLogin)
            .then(() => {
                let state = getState(),
                    redirectUrl = '/settings';

                // Analytics START
                let analytics = new AnalyticsEventsManager();

                analytics.sendLoginEvent({
                    userName: state.user.username,
                    userID: state.user.id
                });

                if (!window.location.href.includes(locationHrefForIgnoringAnalyticsEvent)) { // ignore login event when we on registration pages
                    sendHooligansEvent({
                        event: 'gaEventLogin',
                        gaEventCategory: 'Login',
                        gaEventAction: 'Login Success',
                        gaUserID: state.user.householdId
                    });
                }


                // Analytics END

                dispatch(setError(null));
                dispatch(setStatus(LoginStatus.REQUEST_SUCCESS));

                storageManager.store('user.firstName', state.user.firstName);
                storageManager.store('user.userName', state.user.username);
                storageManager.store('user.id', state.user.id);

                // storageManager.clear('login.email')

                sessionStorage.removeItem('login.email')

                let seasonRegistrationLink = state.seasonRegistration?.registrationLink;

                if (!seasonRegistrationLink) {
                    dispatch(hideModal());
                }

                if (!pureLogin && !preventRedirect && !isMobile()) {

                    // dispatch(fetchLinearChannels());
                    dispatch(setRedirectUrl(redirectUrl));
                    hashHistory.push(redirectUrl);
                }
            })
            .catch((response) => {
                const { result: { error: { message } } } = response,
                    formTitle = document.querySelector('.login .title').innerText;

                sendHooligansEvent({
                    event: 'gaEventLoginProcessError',
                    gaEventCategory: 'Login Process',
                    gaEventAction: formTitle,
                    gaEventLabel: `Error - ${message}`
                });

                dispatch(setStatus(LoginStatus.REQUEST_FAILURE));
                dispatch(setModalStatus(ModalStatus.ERROR));
                return Promise.reject(response);
            });
    };
}

// Exposed elements of the actions file.
export {
    ActionTypes,
    LoginStatus,
    setStatus,
    setFormData,
    setError,
    attemptLogin,
    attemptLogout,
    loginUser,
};



// WEBPACK FOOTER //
// ./src/js/store/actions/modals/login.js